import { useCallback, useEffect, useMemo, useState } from 'react';
import Button from "./Common/Button";
import ZoneModal from "./Common/ZoneModal";
import useFleetStore from "../utils/store";
import ConfirmModal from "./Common/ConfirmModal";
import { assignUnitsToZone, bySerialNumber, getUnitZone, removeUnitsFromZone, withZoneAssigned, withZoneCleared } from "../utils/unitZoneUtils";
import { useIsWriteRestrictedUser } from "../hooks/useIsWriteRestrictedUser";

const BulkUnitSelectionContainer = ({
  children,
  onBulkChangeZone,
  onBulkRemoveFromZone,
  setLoading,
  showToast,
}) => {
  const isActionsDisabled = useIsWriteRestrictedUser();
  const { allPodData, selectedFleet } = useFleetStore();
  const [selectedUnitsForBulkAction, setSelectedUnitsForBulkAction] = useState({});
  const [zoneCountsForBulkActionUnits, setZoneCountsForBulkActionUnits] = useState({});
  const [isDisplayingBulkChangeZoneModal, setIsDisplayingBulkChangeZoneModal] = useState(false);
  const [isDisplayingBulkRemoveFromZoneModal, setIsDisplayingBulkRemoveFromZoneModal] = useState(false);

  const addUnitsToSelectedUnitsForBulkAction = (units) => {
    if (!Array.isArray(units)) {
      units = [units];
    }
    setSelectedUnitsForBulkAction((prev) => {
      const updated = { ...prev };
      units.forEach((unit) => {
        updated[unit.serialNumber] = unit;
      });
      return updated;
    });
    setZoneCountsForBulkActionUnits((prev) => {
      const updated = {...prev};
      units.filter((unit) => !!getUnitZone(unit)).forEach((unit) => {
        const zoneUnid = getUnitZone(unit).unid;
        updated[zoneUnid] = (updated[zoneUnid] || 0) + 1;
      });
      return updated;
    });
  }
  const removeUnitsFromSelectedUnitsForBulkAction = (units) => {
    if (!Array.isArray(units)) {
      units = [units];
    }
    setSelectedUnitsForBulkAction((prev) => {
      const updated = { ...prev };
      units.forEach((unit) => {
        delete updated[unit.serialNumber];
      })
      return updated;
    });
    setZoneCountsForBulkActionUnits((prev) => {
      const updated = {...prev};
      units.filter((unit) => !!getUnitZone(unit)).forEach((unit) => {
        const zoneUnid = getUnitZone(unit).unid;
        if (updated[zoneUnid] === 1) {
          delete updated[zoneUnid];
        } else if (updated[zoneUnid] > 1) {
          updated[zoneUnid] = updated[zoneUnid] - 1;
        }
      });
      return updated;
    });
  }

  const resetSelectionTo = (units) => {
    setSelectedUnitsForBulkAction(bySerialNumber(units));
    setZoneCountsForBulkActionUnits(
      units.reduce((counts, unit) => {
        const zone = getUnitZone(unit);
        if (zone) {
          counts[zone.unid] = (counts[zone.unid] || 0) + 1;
        }
        return counts;
      }, {})
    );
  };

  const handleBulkZoneChange = async (selectedZone) => {
    if (isActionsDisabled) return;
    setIsDisplayingBulkChangeZoneModal(false);
    setLoading(true);
    try {
      const { assigned, failed } = await assignUnitsToZone(
        Object.values(selectedUnitsForBulkAction),
        selectedZone
      );
      if (assigned.length > 0) {
        onBulkChangeZone(bySerialNumber(assigned), selectedZone);
      }
      if (assigned.length === 0) {
        showToast("Bulk zone change failed", 'failed');
      } else if (failed.length > 0) {
        showToast(
          `Changed zone for ${assigned.length} of ${assigned.length + failed.length} units`,
          'failed'
        );
      } else {
        showToast("Successfully changed zone for selected units");
      }
      resetSelectionTo(failed);
    } catch (error) {
      console.error("Bulk zone change failed", error);
      showToast("Bulk zone change failed", 'failed');
    } finally {
      setLoading(false);
    }
  }

  const handleBulkRemoveFromZone = async () => {
    if (isActionsDisabled) return;
    setIsDisplayingBulkRemoveFromZoneModal(false);
    setLoading(true);
    try {
      const { removed, failed } = await removeUnitsFromZone(
        Object.values(selectedUnitsForBulkAction).filter((unit) => !!getUnitZone(unit))
      );
      if (removed.length > 0) {
        onBulkRemoveFromZone(bySerialNumber(removed));
      }
      if (removed.length === 0) {
        showToast("Bulk zone removal failed", 'failed');
      } else if (failed.length > 0) {
        showToast(
          `Removed ${removed.length} of ${removed.length + failed.length} units from their zones`,
          'failed'
        );
      } else {
        showToast(
          `Successfully removed ${removed.length} ${removed.length === 1 ? 'unit from its zone' : 'units from their zones'}`
        );
      }
      resetSelectionTo(failed);
    } catch (error) {
      console.error("Bulk zone removal failed", error);
      showToast("Bulk zone removal failed", 'failed');
    } finally {
      setLoading(false);
    }
  }

  const notifyUnitRemovedFromZone = (serialNumber, fleetUnid) => {
    if (selectedUnitsForBulkAction[serialNumber] !== undefined) {
      setSelectedUnitsForBulkAction((prev) => {
        const updated = { ...prev };
        updated[serialNumber] = withZoneCleared(updated[serialNumber]);
        return updated;
      });
      setZoneCountsForBulkActionUnits((prev) => {
        const updated = {...prev};
        if (updated[fleetUnid] !== undefined && updated[fleetUnid] > 0) {
          updated[fleetUnid] -= 1;
        }
        return updated;
      });
    }
  };

  const notifyUnitChangedZone = (serialNumber, optionalOldZone, newZone) => {
    if (selectedUnitsForBulkAction[serialNumber] !== undefined) {
      setSelectedUnitsForBulkAction((prev) => {
        const updated = { ...prev };
        updated[serialNumber] = withZoneAssigned(updated[serialNumber], newZone);
        return updated;
      });
      setZoneCountsForBulkActionUnits((prev) => {
        const updated = {...prev};
        if (optionalOldZone && updated[optionalOldZone.unid] !== undefined && updated[optionalOldZone.unid] > 0) {
          updated[optionalOldZone.unid] -= 1;
        }
        updated[newZone.unid] = (updated[newZone.unid] || 0) + 1;
        return updated;
      });
    }
  };

  useEffect(() => {
    setSelectedUnitsForBulkAction({});
    setZoneCountsForBulkActionUnits({});
  }, [selectedFleet, setSelectedUnitsForBulkAction, setZoneCountsForBulkActionUnits])

  const allSelectedUnitsBelongToSameZone = useMemo(() => (
    Object.keys(zoneCountsForBulkActionUnits).length === 1 &&
    Object.values(zoneCountsForBulkActionUnits)[0] === Object.keys(selectedUnitsForBulkAction).length
  ), [zoneCountsForBulkActionUnits, selectedUnitsForBulkAction]);
  const isBulkZoneChangeDisabled = useMemo(() => (
    !allPodData?.zones ||
    allPodData.zones.length === 0 ||
    (allSelectedUnitsBelongToSameZone && allPodData.zones.length === 1)
  ), [allPodData?.zones, allSelectedUnitsBelongToSameZone]);
  const numberOfSelectedUnitsWithZone = useMemo(() => (
    Object.values(selectedUnitsForBulkAction).filter((unit) => !!getUnitZone(unit)).length
  ), [selectedUnitsForBulkAction]);
  const isBulkZoneRemovalDisabled = useMemo(() => (
    numberOfSelectedUnitsWithZone === 0
  ), [numberOfSelectedUnitsWithZone]);

  const zoneChoiceFilter = useCallback((zone) => {
    return (
      !allSelectedUnitsBelongToSameZone ||
      zone.unid !== Object.keys(zoneCountsForBulkActionUnits)[0]
    );
  }, [allSelectedUnitsBelongToSameZone, zoneCountsForBulkActionUnits]);

  let bulkActionHeader;
  if (Object.keys(selectedUnitsForBulkAction).length > 0) {
    bulkActionHeader = (
      <div className="flex items-center gap-3 mt-4" data-testid="bulk-action-header">
               <span className="text-[#000000DE] dark:text-white font-semibold" data-testid="bulk-action-selected-count">
             {`${Object.keys(selectedUnitsForBulkAction).length} unit(s) selected`}
                    </span>
        <Button
          data-testid="bulk-action-remove-from-zone-button"
          className={`bg-theme h-10 px-5 py-3 rounded-md text-white text-base font-semibold leading-5 tracking-[-0.32px] min-w-28 flex justify-center items-center ${
            isBulkZoneRemovalDisabled || isActionsDisabled ? "opacity-40 pointer-events-none" : ""
          }`}
          disabled={isBulkZoneRemovalDisabled || isActionsDisabled}
          onClick={() => {
            if (isActionsDisabled) return;
            setIsDisplayingBulkRemoveFromZoneModal(true);
          }}
        >
          Remove From Zone
        </Button>
        <Button
          data-testid="bulk-action-change-zone-button"
          className={`bg-theme h-10 px-5 py-3 rounded-md text-white text-base font-semibold leading-5 tracking-[-0.32px] min-w-28 flex justify-center items-center ${
            isBulkZoneChangeDisabled || isActionsDisabled ? "opacity-40 pointer-events-none" : ""
          }`}
          disabled={isBulkZoneChangeDisabled || isActionsDisabled}
          onClick={() => {
            if (isActionsDisabled) return;
            setIsDisplayingBulkChangeZoneModal(true);
          }}
        >
          Change Zone
        </Button>
      </div>
    );
  }

  return <>
    {
      children({
        bulkActionHeader,
        addUnitsToSelectedUnitsForBulkAction,
        removeUnitsFromSelectedUnitsForBulkAction,
        selectedUnitsForBulkAction,
        notifyUnitRemovedFromZone,
        notifyUnitChangedZone,
      })
    }
    {
      isDisplayingBulkChangeZoneModal && (
        <ZoneModal
          title="Change Zone"
          subText={`${Object.keys(selectedUnitsForBulkAction).length} ${Object.keys(selectedUnitsForBulkAction).length === 1 ? 'unit' : 'units'} will be changed to the selected zone.`}
          confirmMessage={`Are you sure you want to change the zone for the selected ${Object.keys(selectedUnitsForBulkAction).length === 1 ? 'unit' : 'units'}?`}
          cancelText="Cancel"
          submitText="Change"
          onCancel={() => setIsDisplayingBulkChangeZoneModal(false)}
          onSubmit={handleBulkZoneChange}
          shouldSetGloballySelectedFleet={false}
          zoneFilter={zoneChoiceFilter}
        />
      )
    }
    {
      isDisplayingBulkRemoveFromZoneModal && <ConfirmModal
        title="Remove from Zone"
        confirmMessage={`Are you sure you want to remove ${numberOfSelectedUnitsWithZone} ${numberOfSelectedUnitsWithZone === 1 ? 'unit from its zone' : 'units from their zones'}?`}
        cancelText="Cancel"
        submitText="Remove From Zone"
        onCancel={() => setIsDisplayingBulkRemoveFromZoneModal(false)}
        onSubmit={handleBulkRemoveFromZone}
      />
    }
  </>
};

export default BulkUnitSelectionContainer;
